/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unused-vars */
"use client"

import { ColumnDef, RowData } from "@tanstack/react-table"
import { Check, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"
import { format } from "date-fns"
import { dateformat } from "@/utils/GlobalConstants"
import { useContext } from "react"
import { AppContext } from "@/App"
import { PageLockContext } from "@/Components/PageLockProvider"
import { WorkPackageCell } from "@/Components/datatable/cells/workpackage_cell"
import { TextCell } from "@/Components/datatable/cells/text_cell"
import { OwnerCell } from "@/Components/datatable/cells/owner_cell"
import { DateCell } from "@/Components/datatable/cells/date_cell"
import { RatingCell } from "@/Components/datatable/cells/rating_cell"
import { RaisedByCell } from "@/Components/datatable/cells/raisedby_cell"
import { DueDateCell } from "@/Components/datatable/cells/duedate_cell"
import { AcknowledgedIcon, SeenIcon } from "@/Components/ui/Icons/TooltipIcon"

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type ProjectChangeRequest = {
    projectId: number
    projectChangeRequestId: number
    workstreamId: number
    workstream: string
    workPackageId: number
    workPackage: string
    rating: string
    description: string
    owner: string
    userId: string
    date: Date
    raisedBy?: string
    decisionMaker?: string
    dueDate: Date
    closedDate?: Date
    reviewStatus: "Pending" | "Approved" | "Rejected"
    status: "Cancelled" | "Complete" | "Open" | "Closed"
    notes: string
    seen: boolean
    acknowledged: boolean
}

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'date-range' | 'select' | 'bool-select'
    }
}

export function getProjectChangeRequestColumns(HandleDeleteClicked: (ids: number[]) => void,
    editChangeRequest: (id: number) => void,
    setReviewStatus: (reviewStatus: { id: number, status: "Pending" | "Approved" | "Rejected" }[]) => void
): ColumnDef<ProjectChangeRequest>[] {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);

    const columns: ColumnDef<ProjectChangeRequest>[] = [];

    if (app.authLevel > 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && "indeterminate")
                        }
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        onClick={(e) => e.stopPropagation()}
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            }
        );
    }

    columns.push(
        {
            id: "projectChangeRequestId",
            accessorKey: "projectChangeRequestId",
            header: "ID",
            cell: ({ row }) => {
                const projectChangeRequestId = row.getValue("projectChangeRequestId");
                return <div className="text-center">{projectChangeRequestId as number}</div>
            },
            size: 50,
        });

    if (app.selectedProject?.projectTypeId === 2) {

        columns.push({
            id: "workstream",
            accessorKey: "workstream",
            header: "WS",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const workstream = row.getValue("workstream");
                return <div className="text-center">{workstream as string}</div>
            },
            size: 80,
        });
    }
    columns.push(
        {
            id: "workPackage",
            accessorKey: "workPackage",
            header: "WP",
            meta: {
                filterVariant: 'select',
            },
            cell: WorkPackageCell,
            size: 80,
        },
        {
            id: "description",
            accessorKey: "description",
            header: "Change Request",
            cell: TextCell,
            size: 600,
        },
        {
            id: "owner",
            accessorKey: "owner",
            header: "Owner",
            cell: OwnerCell,
        },
        {
            id: "date",
            accessorKey: "date",
            header: "Date Raised",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DateCell,
            filterFn: (row, columnId, filterValue) => {
                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
        },
        {
            id: "raisedBy",
            accessorKey: "raisedBy",
            header: "Raised By",
            cell: RaisedByCell,
        },
        {
            id: "decisionMaker",
            accessorKey: "decisionMaker",
            header: "Decision Maker",
            cell: RaisedByCell, // raisedByCell is used when no userId is required, name only
        },
        {
            id: "dueDate",
            accessorKey: "dueDate",
            header: "Due Date",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DueDateCell,
            filterFn: (row, columnId, filterValue) => {
                if (row.getValue(columnId) == null) return true; // if its null just return true as cant be filtered

                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
        },
        {
            id: "closedDate",
            accessorKey: "closedDate",
            header: "Decision Date",
            meta: {
                filterVariant: 'date-range',
            },
            cell: ({ row }) => {
                if (row.getValue("closedDate") === null) return <div className="text-center">-</div>
                const dueDate = new Date(row.getValue("closedDate"));
                const today = new Date();
                let style = "";
                if (dueDate.getTime() < today.getTime() && row.getValue("status") === "Open") {
                    style = " text-red-500";
                }
                return <div className={"text-center" + style}>{format((dueDate as Date), dateformat)}</div>
            },
            filterFn: (row, columnId, filterValue) => {
                if (row.getValue(columnId) == null) return true; // if its null just return true as cant be filtered

                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
        },
        {
            id: "rating",
            accessorKey: "rating",
            header: "Rating",
            meta: {
                filterVariant: 'select',
            },
            cell: RatingCell,
        },
        {
            id: "reviewStatus",
            accessorKey: "reviewStatus",
            header: "Outcome",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const status = row.getValue("reviewStatus");
                let style = "bg-gray-500"
                if (status === "Pending") {
                    style = "bg-gray-500"
                } else if (status === "Approved") {
                    style = "bg-chart-green"
                } else if (status === "Rejected") {
                    style = "bg-chart-red"
                }
                return <div className={"text-center text-white " + style}>{status as string}</div>
            },
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const status = row.getValue("status");
                let style = "bg-chart-amber"
                if (status === "Closed") {
                    style = "bg-chart-green"
                } else if (status === "Cancelled") {
                    style = "bg-gray-500"
                } else if (status === "Complete") {
                    style = "bg-blue-600"
                }
                return <div className={"text-center text-white " + style}>{status as string}</div>
            },
        },
        {
            id: "notes",
            accessorKey: "notes",
            header: "Notes",
            cell: TextCell,
        },
        {
            id: "seen",
            accessorKey: "seen",
            header: () => {
                return (<SeenIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const seen = row.getValue("seen");
                return <>{seen ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "acknowledged",
            accessorKey: "acknowledged",
            header: () => {
                return (<AcknowledgedIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const acknowledged = row.getValue("acknowledged");
                return <>{acknowledged ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        }
    );

    if (app.authLevel > 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "actions",
                cell: ({ row }) => {
                    const ProjectChangeRequest = row.original

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem
                                    onClick={(e) => {e.stopPropagation(); setReviewStatus([{ id: ProjectChangeRequest.projectChangeRequestId, status: "Approved" }])}}
                                >
                                    Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={(e) => {e.stopPropagation(); setReviewStatus([{ id: ProjectChangeRequest.projectChangeRequestId, status: "Rejected" }])}}
                                >
                                    Reject
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={(e) => {e.stopPropagation(); editChangeRequest(ProjectChangeRequest.projectChangeRequestId)}}
                                >
                                    Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); HandleDeleteClicked([ProjectChangeRequest.projectChangeRequestId])}}
                                >
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )
                },
            }
        );
    }

    return columns
}

export const columnDisplayNames: [string, string][] = [
    ["projectChangeRequestId", "ID"],
    ["workstream", "Workstream"],
    ["workPackage", "Work Package"],
    ["description", "Change Request"],
    ["requestedFrom", "Requested From"],
    ["date", "Date"],
    ["raisedBy", "Raised By"],
    ["decisionMaker", "Decision Maker"],
    ["dueDate", "Due Date"],
    ["closedDate", "Decision Date"],
    ["rating", "Rating"],
    ["reviewStatus", "Outcome"],
    ["status", "Status"],
    ["notes", "Notes"]
]